import {useEffect,useRef} from 'react';
import './ParticleText.css';

const clamp=(value,min,max)=> Math.min(Math.max(value,min),max);
const easeOutCubic=(value)=> 1 - Math.pow(1 - value,3);

function hexToRgb(hex) {
  const clean=hex.replace('#','').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;
  return {
    r: parseInt(clean.slice(0,2),16),
    g: parseInt(clean.slice(2,4),16),
    b: parseInt(clean.slice(4,6),16)
  };
}

function mixColor(from,to,amount) {
  if (!from || !to) return '#ffffff';
  const mix=(start,end)=> Math.round(start + (end - start) * amount);
  return `rgb(${mix(from.r,to.r)},${mix(from.g,to.g)},${mix(from.b,to.b)})`;
}

export default function ParticleText({
  text='DADDA COOL',
  particleSize=1.4,
  density=3,
  color='#ffffff',
  highlightColor='#64e8ce',
  scatter=42,
  gatherDuration=850,
  stagger=180,
  pointerRepel=12,
  repelRadius=42,
  idleDrift=0.25,
  trigger='hover',
  fontSize=25,
  fontWeight=800,
  fontFamily='Space Grotesk',
  glow=true,
  className='',
  style
}) {
  const containerRef=useRef(null);
  const canvasRef=useRef(null);

  useEffect(()=> {
    const container=containerRef.current;
    const canvas=canvasRef.current;
    const context=canvas?.getContext('2d');
    if (!container || !canvas || !context) return undefined;

    let particles=[];
    let frame;
    let resizeFrame;
    let gathering=true;
    let gatherStart=performance.now();
    let width=0;
    let height=0;
    const reduced=window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    const pointer={active: false,x: 0,y: 0};

    const scatterParticles=()=> {
      if (reduced) return;
      particles.forEach((particle)=> {
        const angle=particle.seed * Math.PI * 2;
        const distance=scatter * (0.45 + particle.seed * 0.55);
        particle.x=particle.targetX + Math.cos(angle) * distance;
        particle.y=particle.targetY + Math.sin(angle) * distance;
        particle.startX=particle.x;
        particle.startY=particle.y;
        particle.delay=particle.seed * stagger;
      });
      gatherStart=performance.now();
      gathering=true;
    };

    const build=()=> {
      const rect=container.getBoundingClientRect();
      width=Math.max(1,Math.floor(rect.width));
      height=Math.max(1,Math.floor(rect.height));
      const dpr=Math.min(window.devicePixelRatio || 1,2);

      canvas.width=Math.floor(width * dpr);
      canvas.height=Math.floor(height * dpr);
      context.setTransform(dpr,0,0,dpr,0,0);

      const offscreen=document.createElement('canvas');
      const offContext=offscreen.getContext('2d',{willReadFrequently: true});
      const size=Number(fontSize) || 25;
      const font=`${fontWeight} ${size}px "${fontFamily}", sans-serif`;

      offContext.font=font;
      const metrics=offContext.measureText(text);
      const textWidth=Math.ceil(metrics.width);
      const textHeight=Math.ceil(size * 1.35);
      offscreen.width=textWidth + 8;
      offscreen.height=textHeight + 8;
      offContext.font=font;
      offContext.textAlign='center';
      offContext.textBaseline='middle';
      offContext.fillStyle='#fff';
      offContext.fillText(text,offscreen.width / 2,offscreen.height / 2);

      const pixels=offContext.getImageData(
        0,0,offscreen.width,offscreen.height
      ).data;
      const primary=hexToRgb(color);
      const highlight=hexToRgb(highlightColor);
      const next=[];
      const step=Math.max(2,Math.floor(density));

      for (let y=0;y < offscreen.height;y +=step) {
        for (let x=0;x < offscreen.width;x +=step) {
          const alpha=pixels[(y * offscreen.width + x) * 4 + 3];
          if (alpha < 55) continue;
          const seed=((next.length * 9301 + 49297) % 233280) / 233280;
          const targetX=(width - offscreen.width) / 2 + x;
          const targetY=(height - offscreen.height) / 2 + y;
          next.push({
            x: targetX,
            y: targetY,
            startX: targetX,
            startY: targetY,
            targetX,
            targetY,
            seed,
            delay: seed * stagger,
            size: particleSize * (0.82 + alpha / 255 * 0.3),
            color: mixColor(primary,highlight,clamp(x / offscreen.width,0,1))
          });
        }
      }

      particles=next;
      pointer.x=width / 2;
      pointer.y=height / 2;

      if (reduced) {
        gathering=false;
      } else {
        scatterParticles();
      }
    };

    const render=(now)=> {
      context.clearRect(0,0,width,height);
      context.shadowBlur=glow && !reduced ? 4 : 0;
      context.shadowColor=highlightColor;
      let complete=true;

      particles.forEach((particle)=> {
        let baseX=particle.targetX;
        let baseY=particle.targetY;

        if (gathering) {
          const progress=clamp(
            (now - gatherStart - particle.delay) / gatherDuration,
            0,
            1
          );
          const eased=easeOutCubic(progress);
          baseX=particle.startX +
            (particle.targetX - particle.startX) * eased;
          baseY=particle.startY +
            (particle.targetY - particle.startY) * eased;
          if (progress < 1) complete=false;
        } else if (!reduced) {
          baseX +=Math.sin(now * 0.001 + particle.seed * 8) * idleDrift;
          baseY +=Math.cos(now * 0.0008 + particle.seed * 9) * idleDrift;
        }

        if (pointer.active && !reduced) {
          const dx=baseX - pointer.x;
          const dy=baseY - pointer.y;
          const distance=Math.hypot(dx,dy);
          if (distance > 0 && distance < repelRadius) {
            const force=Math.pow(1 - distance / repelRadius,2) * pointerRepel;
            baseX +=dx / distance * force;
            baseY +=dy / distance * force;
          }
        }

        particle.x +=(baseX - particle.x) * (reduced ? 1 : 0.24);
        particle.y +=(baseY - particle.y) * (reduced ? 1 : 0.24);
        context.fillStyle=particle.color;
        context.fillRect(
          particle.x - particle.size / 2,
          particle.y - particle.size / 2,
          particle.size,
          particle.size
        );
      });

      if (gathering && complete) gathering=false;
      frame=requestAnimationFrame(render);
    };

    const move=(event)=> {
      const rect=canvas.getBoundingClientRect();
      pointer.x=event.clientX - rect.left;
      pointer.y=event.clientY - rect.top;
      pointer.active=true;
    };
    const enter=(event)=> {
      move(event);
      if (trigger==='hover') scatterParticles();
    };
    const leave=()=> {
      pointer.active=false;
    };
    const click=()=> {
      if (trigger==='click') scatterParticles();
    };
    const queueBuild=()=> {
      cancelAnimationFrame(resizeFrame);
      resizeFrame=requestAnimationFrame(build);
    };

    const observer=new ResizeObserver(queueBuild);
    observer.observe(container);
    canvas.addEventListener('pointerenter',enter);
    canvas.addEventListener('pointermove',move);
    canvas.addEventListener('pointerleave',leave);
    canvas.addEventListener('click',click);

    document.fonts?.ready.then(build);
    build();
    frame=requestAnimationFrame(render);

    return ()=> {
      observer.disconnect();
      cancelAnimationFrame(frame);
      cancelAnimationFrame(resizeFrame);
      canvas.removeEventListener('pointerenter',enter);
      canvas.removeEventListener('pointermove',move);
      canvas.removeEventListener('pointerleave',leave);
      canvas.removeEventListener('click',click);
    };
  },[
    text,particleSize,density,color,highlightColor,scatter,gatherDuration,
    stagger,pointerRepel,repelRadius,idleDrift,trigger,fontSize,fontWeight,
    fontFamily,glow
  ]);

  return (
    <span
      ref={containerRef}
      className={`particle-text ${className}`}
      style={style}
      aria-label={text}
    >
      <canvas
        ref={canvasRef}
        className="particle-text__canvas"
        aria-hidden="true"
      />
      <span className="particle-text__sr">{text}</span>
    </span>
  );
}